import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, FileText, User as UserIcon, AlertCircle, CheckCircle, Clock, XCircle, X, Trash2, Plus, Mail, Phone, ChevronDown, Search } from 'lucide-react';
import { vendorInvoicesAPI, vendorsAPI, departmentsAPI, currenciesAPI } from '../services/api';
import { VendorInvoice, Vendor, User, Currency } from '../types';
import { formatCurrency, getCurrencySymbol, getCurrencyFontClass } from '../utils/currency';
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import Spinner from '../components/Spinner';
import { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';

const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const InvoiceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState<VendorInvoice | null>(null);
  const [auditTrail, setAuditTrail] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving] = useState(false);
  const [isSendingForApproval, setIsSendingForApproval] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [invoiceApprovers, setInvoiceApprovers] = useState<User[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  // const [departments, setDepartments] = useState<Department[]>([]);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const additionalAttachmentsInputRef = useRef<HTMLInputElement>(null);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [approverNotes, setApproverNotes] = useState<{ [key: number]: string }>({});

  // Searchable dropdown states
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const vendorDropdownRef = useRef<HTMLDivElement>(null);
  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);
  // const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const departmentDropdownRef = useRef<HTMLDivElement>(null);
  const [approverDropdownOpenIndex, setApproverDropdownOpenIndex] = useState<number | null>(null);
  const [approverSearchTerms, setApproverSearchTerms] = useState<string[]>(['']);
  const approverDropdownRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [editForm, setEditForm] = useState({
    department_id: '',
    vendor_id: '',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    total_amount: '',
    subtotal: '',
    tax_amount: '',
    description: '',
    notes: '',
    po_number: '',
    currency: '',
    file: null as File | null,
    additional_attachments: [] as File[],
    approvers: [''] as string[],
    payment_approver: '',
    initiator_note: '',
    payment_notes: '',
    reference_name: '',
    // Nastro account details (array) - initialize with one empty entry
    nastro_accounts: [{
      account_no: '',
      account_name: '',
      narration: ''
    }] as Array<{
      account_no: string;
      account_name: string;
      narration: string;
    }>,
    // Credit account details (array) - initialize with one empty entry
    credit_accounts: [{
      bank_name: '',
      beneficiary_name: '',
      account_no: '',
      sort_code: '',
      iban_no: '',
      bic: '',
      amount: '',
      narration: ''
    }] as Array<{
      bank_name: string;
      beneficiary_name: string;
      account_no: string;
      sort_code: string;
      iban_no: string;
      bic: string;
      amount: string;
      narration: string;
    }>,
  });

  const [editableLineItems, setEditableLineItems] = useState<Array<{
    id?: number;
    description: string;
    quantity: number;
    unit_price: number;
    amount: number;
    isNew?: boolean;
  }>>([]);

  const [validationMessages, setValidationMessages] = useState<{
    invoice_number?: string;
    approvers?: string;
  }>({});

  // Field-level errors for account entries
  const [nastroAccountErrors, setNastroAccountErrors] = useState<{ [index: number]: { account_no?: string; account_name?: string } }>({});
  const [creditAccountErrors, setCreditAccountErrors] = useState<{ [index: number]: { bank_name?: string; beneficiary_name?: string; account_no?: string; sort_code?: string; iban_no?: string; bic?: string; amount?: string } }>({});

  const [fieldErrors, setFieldErrors] = useState<{
    department_id?: string;
    vendor_id?: string;
    invoice_number?: string;
    invoice_date?: string;
    due_date?: string;
    subtotal?: string;
    tax_amount?: string;
    total_amount?: string;
    currency?: string;
    reference_name?: string;
    nastro_accounts?: string;
    credit_accounts?: string;
  }>({});

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      if (!id) {
        setError('Invoice ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch invoice details
        const invoiceResponse = await vendorInvoicesAPI.getById(parseInt(id));
        if (invoiceResponse.success) {
          setInvoice(invoiceResponse.data);
          // Reset approver notes when invoice is loaded
          setApproverNotes({});
          // Initialize edit form when invoice is loaded
          const invoiceData = invoiceResponse.data;

          // Initialize approvers array from approver_details or legacy fields
          let initialApprovers: string[] = [];
          if (invoiceData.approver_details && invoiceData.approver_details.length > 0) {
            initialApprovers = invoiceData.approver_details.map((a: any) => a.approver_id?.toString() || '');
          } else {
            // Fallback to legacy approver fields
            if ((invoiceData as any).approver_1) initialApprovers.push((invoiceData as any).approver_1.toString());
            if ((invoiceData as any).approver_2) initialApprovers.push((invoiceData as any).approver_2.toString());
            if ((invoiceData as any).approver_3) initialApprovers.push((invoiceData as any).approver_3.toString());
          }
          if (initialApprovers.length === 0) initialApprovers = [''];

          setEditForm({
            department_id: invoiceData.department_id?.toString() || '',
            vendor_id: invoiceData.vendor_id?.toString() || '',
            invoice_number: invoiceData.invoice_number,
            invoice_date: new Date(invoiceData.invoice_date).toISOString().split('T')[0],
            due_date: new Date(invoiceData.due_date).toISOString().split('T')[0],
            total_amount: invoiceData.total_amount.toString(),
            subtotal: invoiceData.subtotal?.toString() || '',
            tax_amount: invoiceData.tax_amount?.toString() || '',
            description: invoiceData.description || '',
            notes: invoiceData.notes || '',
            po_number: invoiceData.po_number || '',
            currency: invoiceData.currency || '',
            file: null,
            additional_attachments: [],
            approvers: initialApprovers,
            payment_approver: (invoiceData as any).payment_approver?.toString?.() || (invoiceData as any).payment_approver_id?.toString?.() || '',
            initiator_note: invoiceData.initiator_note || '',
            payment_notes: invoiceData.payment_notes || '',
            reference_name: invoiceData.reference_name || '',
            // Nastro account details (array) - handle both array and single value for backward compatibility, ensure at least one entry
            nastro_accounts: (() => {
              const accounts = invoiceData.nastro_accounts 
                ? (Array.isArray(invoiceData.nastro_accounts) ? invoiceData.nastro_accounts : [invoiceData.nastro_accounts])
                : (invoiceData.nastro_account_no ? [{
                    account_no: invoiceData.nastro_account_no || '',
                    account_name: invoiceData.nastro_account_name || '',
                    narration: invoiceData.nastro_narration || ''
                  }] : []);
              return accounts.length > 0 ? accounts : [{
                account_no: '',
                account_name: '',
                narration: ''
              }];
            })(),
            // Credit account details (array) - handle both array and single value for backward compatibility, ensure at least one entry
            credit_accounts: (() => {
              const accounts = invoiceData.credit_accounts
                ? (Array.isArray(invoiceData.credit_accounts) ? invoiceData.credit_accounts : [invoiceData.credit_accounts])
                : (invoiceData.credit_bank_name ? [{
                    bank_name: invoiceData.credit_bank_name || '',
                    beneficiary_name: invoiceData.credit_beneficiary_name || '',
                    account_no: invoiceData.credit_account_no || '',
                    sort_code: invoiceData.credit_sort_code || '',
                    iban_no: invoiceData.credit_iban_no || '',
                    bic: invoiceData.credit_bic || '',
                    amount: invoiceData.credit_amount || '',
                    narration: invoiceData.credit_narration || ''
                  }] : []);
              return accounts.length > 0 ? accounts : [{
                bank_name: '',
                beneficiary_name: '',
                account_no: '',
                sort_code: '',
                iban_no: '',
                bic: '',
                amount: '',
                narration: ''
              }];
            })()
          });
          setApproverSearchTerms(initialApprovers.map(() => ''));

          // Initialize line items for editing
          setEditableLineItems(
            invoiceData.line_items?.map((item: any) => ({
              id: item.id,
              description: item.description,
              quantity: item.quantity,
              unit_price: item.unit_price,
              amount: item.amount,
              isNew: false
            })) || []
          );

          const currenciesResponse = await currenciesAPI.dropDown();
          if (currenciesResponse.success) {
            const currencyList = currenciesResponse.data || [];
            setCurrencies(currencyList);

            if (!invoiceData.currency) {
              const defaultCurrency =
                currencyList.find((currency: Currency) => currency.is_default)?.name ||
                currencyList[0]?.name ||
                '';

              if (defaultCurrency) {
                setEditForm((prev) => ({ ...prev, currency: prev.currency || defaultCurrency }));
                setInvoice((prev) => (prev ? { ...prev, currency: defaultCurrency } : prev));
              }
            }
          } else {
            setCurrencies([]);
          }
        } else {
          throw new Error(invoiceResponse.message || 'Failed to fetch invoice details');
        }

        // Fetch audit trail
        const auditResponse = await vendorInvoicesAPI.getAuditTrail(parseInt(id));
        if (auditResponse.success) {
          setAuditTrail(auditResponse.data);
        }

        // Fetch departments for dropdown
        const departmentsResponse = await departmentsAPI.dropDown();
        if (departmentsResponse.success) {
          // setDepartments(departmentsResponse.data);
        }

        // Fetch approvers for edit dropdown
        const invoiceApproversResponse = await vendorInvoicesAPI.invoiceApproverDropdown();
        if (invoiceApproversResponse.success) {
          setInvoiceApprovers(invoiceApproversResponse.data);
        }

      } catch (err: any) {
        console.error('Error fetching invoice details:', err);
        setError(err.message || 'Failed to load invoice details');
        errorModal('Failed to load invoice details');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
  }, [id]);


  // Debounced validation for invoice number duplicate check (exclude the current invoice's own number)
  useEffect(() => {
    if (!editForm.vendor_id || !editForm.invoice_number.trim()) {
      setValidationMessages(prev => ({ ...prev, invoice_number: undefined }));
      return;
    }

    // If unchanged from the original invoice (and same vendor), do not flag duplicate
    if (
      invoice &&
      String(invoice.vendor_id || '') === String(editForm.vendor_id) &&
      String(invoice.invoice_number || '') === String(editForm.invoice_number).trim()
    ) {
      setValidationMessages(prev => ({ ...prev, invoice_number: undefined }));
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const response = await vendorInvoicesAPI.checkDuplicateInvoice(
          Number(editForm.vendor_id),
          editForm.invoice_number.trim()
        );

        if (response.success && response.message === 'Invoice number already exists') {
          setValidationMessages(prev => ({ ...prev, invoice_number: 'This invoice number already exists for this vendor' }));
        } else {
          setValidationMessages(prev => ({ ...prev, invoice_number: undefined }));
        }
      } catch (error) {
        console.error('Error checking duplicate invoice:', error);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [editForm.vendor_id, editForm.invoice_number, invoice]);

  // Fetch all vendors
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await vendorsAPI.allVendorsDropdown();
        if (response.success) {
          setVendors(response.data);
          // Clear vendor selection if current vendor is not in the filtered list
          if (editForm.vendor_id) {
            const vendorExists = response.data.some((v: Vendor) => v.id.toString() === editForm.vendor_id);
            if (!vendorExists) {
              setEditForm(prev => ({ ...prev, vendor_id: '' }));
            }
          }
        } else {
          setVendors([]);
          setEditForm(prev => ({ ...prev, vendor_id: '' }));
        }
      } catch (err) {
        setVendors([]);
        setEditForm(prev => ({ ...prev, vendor_id: '' }));
      }
    };
    fetchVendors();
  }, []);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target as Node)) {
        setVendorDropdownOpen(false);
        setVendorSearchTerm('');
      }
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target as Node)) {
        setDepartmentDropdownOpen(false);
        // setDepartmentSearchTerm('');
      }
      if (
        approverDropdownOpenIndex !== null &&
        approverDropdownRefs.current[approverDropdownOpenIndex] &&
        !approverDropdownRefs.current[approverDropdownOpenIndex]?.contains(event.target as Node)
      ) {
        setApproverDropdownOpenIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [approverDropdownOpenIndex]);

  const handleCancelEdit = () => {
    navigate('/invoices');
    setEditForm({
      department_id: '',
      vendor_id: '',
      invoice_number: '',
      invoice_date: '',
      due_date: '',
      total_amount: '',
      subtotal: '',
      tax_amount: '',
      description: '',
      notes: '',
      po_number: '',
      currency: '',
      file: null,
      additional_attachments: [],
      approvers: [''],
      payment_approver: '',
      initiator_note: '',
      payment_notes: '',
      reference_name: '',
      // Nastro account details (array) - initialize with one empty entry
      nastro_accounts: [{
        account_no: '',
        account_name: '',
        narration: ''
      }],
      // Credit account details (array) - initialize with one empty entry
      credit_accounts: [{
        bank_name: '',
        beneficiary_name: '',
        account_no: '',
        sort_code: '',
        iban_no: '',
        bic: '',
        amount: '',
        narration: ''
      }]
    });
    setApproverSearchTerms(['']);
    setEditableLineItems([]);
    setNastroAccountErrors({});
    setCreditAccountErrors({});
    if (editFileInputRef.current) {
      editFileInputRef.current.value = '';
    }
    if (additionalAttachmentsInputRef.current) {
      additionalAttachmentsInputRef.current.value = '';
    }
  };

  const validateFields = () => {
    const errors: typeof fieldErrors = {};

    // if (!editForm.department_id) {
    //   errors.department_id = 'Department is required';
    // }

    if (!editForm.vendor_id) {
      errors.vendor_id = 'Vendor is required';
    }

    if (!editForm.invoice_number || !editForm.invoice_number.trim()) {
      errors.invoice_number = 'Invoice number is required';
    }

    if (!editForm.invoice_date) {
      errors.invoice_date = 'Invoice date is required';
    }

    if (!editForm.due_date) {
      errors.due_date = 'Due date is required';
    }

    if (!editForm.subtotal || editForm.subtotal === '0' || editForm.subtotal === '') {
      errors.subtotal = 'Amount (Excluding Vat/Tax) is required';
    }

    if (!editForm.tax_amount || editForm.tax_amount === '') {
      errors.tax_amount = 'Vat/Tax amount is required';
    }

    if (!editForm.total_amount || editForm.total_amount === '0' || editForm.total_amount === '') {
      errors.total_amount = 'Total amount is required';
    }

    if (!editForm.currency) {
      errors.currency = 'Currency is required';
    }

    // Reference Name validation - required only if approval type is "Financial approval + Invoice Payment"
    if (invoice?.approval_type === 'Financial approval + Invoice Payment') {
      if (!editForm.reference_name || !editForm.reference_name.trim()) {
        errors.reference_name = 'Reference name is required';
      }
    }

    // Nastro Account Details validation - Account No and Account Name are mandatory for each entry
    const nastroErrors: { [index: number]: { account_no?: string; account_name?: string } } = {};
    editForm.nastro_accounts.forEach((account, index) => {
      const entryErrors: { account_no?: string; account_name?: string } = {};
      if (!account.account_no || !account.account_no.trim()) {
        entryErrors.account_no = 'Account No is required';
      }
      if (!account.account_name || !account.account_name.trim()) {
        entryErrors.account_name = 'Account Name is required';
      }
      if (Object.keys(entryErrors).length > 0) {
        nastroErrors[index] = entryErrors;
      }
    });
    if (Object.keys(nastroErrors).length > 0) {
      setNastroAccountErrors(nastroErrors);
      errors.nastro_accounts = 'Please fill in all required fields for nostro accounts';
    } else {
      setNastroAccountErrors({});
    }

    // Credit Account Details validation - Bank Name, Beneficiary Name, Account No, and Amount are mandatory for each entry
    const creditErrors: { [index: number]: { bank_name?: string; beneficiary_name?: string; account_no?: string; sort_code?: string; iban_no?: string; bic?: string; amount?: string } } = {};
    editForm.credit_accounts.forEach((account, index) => {
      const entryErrors: { bank_name?: string; beneficiary_name?: string; account_no?: string; sort_code?: string; iban_no?: string; bic?: string; amount?: string } = {};
      if (!account.bank_name || !account.bank_name.trim()) {
        entryErrors.bank_name = 'Bank Name is required';
      }
      if (!account.beneficiary_name || !account.beneficiary_name.trim()) {
        entryErrors.beneficiary_name = 'Beneficiary Name is required';
      }
      if (!account.account_no || !account.account_no.trim()) {
        entryErrors.account_no = 'Account No is required';
      }
      // Sort Code, IBAN No, and BIC are optional fields
      if (!account.amount || !account.amount.trim()) {
        entryErrors.amount = 'Amount is required';
      }
      if (Object.keys(entryErrors).length > 0) {
        creditErrors[index] = entryErrors;
      }
    });
    if (Object.keys(creditErrors).length > 0) {
      setCreditAccountErrors(creditErrors);
      errors.credit_accounts = 'Please fill in all required fields for credit accounts';
    } else {
      setCreditAccountErrors({});
    }

    // Validate that sum of credit account amounts matches total amount
    if (editForm.credit_accounts && editForm.credit_accounts.length > 0) {
      const sumOfCreditAmounts = editForm.credit_accounts.reduce((sum, account) => {
        const amount = parseFloat(account.amount) || 0;
        return sum + amount;
      }, 0);
      
      const totalAmount = parseFloat(editForm.total_amount) || 0;
      
      // Allow small floating point differences (0.01)
      if (Math.abs(sumOfCreditAmounts - totalAmount) > 0.01) {
        errors.total_amount = `Sum of Credit Account amounts (${sumOfCreditAmounts.toFixed(2)}) does not match Total Amount (${totalAmount.toFixed(2)})`;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
  //   e.preventDefault();
  //   if (!invoice) return;

  //   // Validate required fields
  //   if (!validateFields()) {
  //     // errorModal('Please fill in all required fields');
  //     return;
  //   }

  //   // Skip approver validation for Save button
  //   // Clear approver validation errors if they exist
  //   setValidationMessages(prev => {
  //     const { approver_1, approver_2, approver_3, ...rest } = prev;
  //     return rest;
  //   });

  //   setIsSaving(true);
  //   try {
  //     const formData = new FormData();
  //     formData.append('department', editForm.department_id || '');
  //     formData.append('vendor_id', editForm.vendor_id);
  //     formData.append('invoice_number', editForm.invoice_number);
  //     formData.append('invoice_date', editForm.invoice_date);
  //     formData.append('due_date', editForm.due_date);
  //     formData.append('total_amount', editForm.total_amount);
  //     formData.append('subtotal', editForm.subtotal || '');
  //     formData.append('tax_amount', editForm.tax_amount || '');
  //     formData.append('description', editForm.description || '');
  //     formData.append('notes', editForm.notes || '');
  //     formData.append('po_number', editForm.po_number || '');
  //     formData.append('approver_1', editForm.approver_1 || '');
  //     formData.append('approver_2', editForm.approver_2 || '');
  //     formData.append('approver_3', editForm.approver_3 || '');
  //     formData.append('payment_approver', editForm.payment_approver || '');
  //     formData.append('initiator_note', editForm.initiator_note || '');
  //     formData.append('payment_notes', editForm.payment_notes || '');

  //     // Add line items data
  //     formData.append('line_items', JSON.stringify(editableLineItems));

  //     if (editForm.file) {
  //       formData.append('invoice_file', editForm.file);
  //     }

  //     if (editForm.additional_attachments && editForm.additional_attachments.length > 0) {
  //       editForm.additional_attachments.forEach((file) => {
  //         formData.append('additional_attachments', file);
  //       });
  //     }

  //     const response = await vendorInvoicesAPI.update(invoice.id, formData);
  //     if (response.success) {
  //       toast.success('Invoice saved successfully');
  //       // Refresh the invoice data
  //       const updatedResponse = await vendorInvoicesAPI.getById(parseInt(id!));
  //       if (updatedResponse.success) {
  //         setInvoice(updatedResponse.data);
  //       }
  //     } else {
  //       errorModal(response.error || 'Error saving invoice. Please try again.');
  //     }
  //   } catch (error) {
  //     if (error instanceof AxiosError && error.response?.status === 400) {
  //       errorModal(error.response?.data?.error || 'Error saving invoice. Please try again.');
  //     } else {
  //       errorModal('Error saving invoice. Please try again.');
  //     }
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleAddApprover = () => {
    if (editForm.approvers.length >= 10) {
      errorModal('Maximum 10 approvers allowed');
      return;
    }
    setEditForm((prev) => ({
      ...prev,
      approvers: [...prev.approvers, '']
    }));
    setApproverSearchTerms((prev) => [...prev, '']);
  };

  const handleRemoveApprover = (index: number) => {
    if (editForm.approvers.length <= 1) {
      errorModal('At least one approver is required');
      return;
    }
    setEditForm((prev) => ({
      ...prev,
      approvers: prev.approvers.filter((_, i) => i !== index)
    }));
    setApproverSearchTerms((prev) => prev.filter((_, i) => i !== index));
    setApproverDropdownOpenIndex(null);
  };

  const handleApproverSelect = (index: number, approverId: string) => {
    setEditForm((prev) => {
      const newApprovers = [...prev.approvers];
      newApprovers[index] = approverId;
      return { ...prev, approvers: newApprovers };
    });
    setApproverDropdownOpenIndex(null);
    setApproverSearchTerms((prev) => {
      const newTerms = [...prev];
      newTerms[index] = '';
      return newTerms;
    });
    setValidationMessages(prev => ({ ...prev, approvers: undefined }));
  };

  // Nastro account management functions
  const addNastroAccount = () => {
    setEditForm((prev) => ({
      ...prev,
      nastro_accounts: [...prev.nastro_accounts, { account_no: '', account_name: '', narration: '' }]
    }));
  };

  const removeNastroAccount = (index: number) => {
    if (editForm.nastro_accounts.length <= 1) {
      errorModal('At least one nostro account is required');
      return;
    }
    setEditForm((prev) => ({
      ...prev,
      nastro_accounts: prev.nastro_accounts.filter((_, i) => i !== index)
    }));
  };

  const updateNastroAccount = (index: number, field: string, value: string) => {
    setEditForm((prev) => {
      const updated = [...prev.nastro_accounts];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, nastro_accounts: updated };
    });
  };

  // Credit account management functions
  const addCreditAccount = () => {
    setEditForm((prev) => ({
      ...prev,
      credit_accounts: [...prev.credit_accounts, {
        bank_name: '',
        beneficiary_name: '',
        account_no: '',
        sort_code: '',
        iban_no: '',
        bic: '',
        amount: '',
        narration: ''
      }]
    }));
  };

  const removeCreditAccount = (index: number) => {
    if (editForm.credit_accounts.length <= 1) {
      errorModal('At least one credit account is required');
      return;
    }
    setEditForm((prev) => {
      const updatedAccounts = prev.credit_accounts.filter((_, i) => i !== index);
      
      // Validate that sum of credit account amounts matches total amount after removal
      if (updatedAccounts.length > 0) {
        const sumOfCreditAmounts = updatedAccounts.reduce((sum, account) => {
          const amount = parseFloat(account.amount) || 0;
          return sum + amount;
        }, 0);
        
        const totalAmount = parseFloat(prev.total_amount) || 0;
        
        // Allow small floating point differences (0.01)
        if (Math.abs(sumOfCreditAmounts - totalAmount) > 0.01) {
          setFieldErrors(prevErrors => ({
            ...prevErrors,
            total_amount: `Sum of Credit Account amounts (${sumOfCreditAmounts.toFixed(2)}) does not match Total Amount (${totalAmount.toFixed(2)})`
          }));
        } else {
          // Clear the error if amounts match
          setFieldErrors(prevErrors => {
            const { total_amount, ...rest } = prevErrors;
            // Only clear if the error was about amount mismatch
            if (total_amount && total_amount.includes('Sum of Credit Account amounts')) {
              return rest;
            }
            return prevErrors;
          });
        }
      }
      
      return {
        ...prev,
        credit_accounts: updatedAccounts
      };
    });
  };

  const updateCreditAccount = (index: number, field: string, value: string) => {
    setEditForm((prev) => {
      const updated = [...prev.credit_accounts];
      updated[index] = { ...updated[index], [field]: value };
      const newForm = { ...prev, credit_accounts: updated };
      
      // Validate that sum of credit account amounts matches total amount (if amount field is being updated)
      if (field === 'amount' && updated.length > 0) {
        const sumOfCreditAmounts = updated.reduce((sum, account) => {
          const amount = parseFloat(account.amount) || 0;
          return sum + amount;
        }, 0);
        
        const totalAmount = parseFloat(prev.total_amount) || 0;
        
        // Allow small floating point differences (0.01)
        if (Math.abs(sumOfCreditAmounts - totalAmount) > 0.01) {
          setFieldErrors(prevErrors => ({
            ...prevErrors,
            total_amount: `Sum of Credit Account amounts (${sumOfCreditAmounts.toFixed(2)}) does not match Total Amount (${totalAmount.toFixed(2)})`
          }));
        } else {
          // Clear the error if amounts match
          setFieldErrors(prevErrors => {
            const { total_amount, ...rest } = prevErrors;
            // Only clear if the error was about amount mismatch
            if (total_amount && total_amount.includes('Sum of Credit Account amounts')) {
              return rest;
            }
            return prevErrors;
          });
        }
      }
      
      return newForm;
    });
    // Clear validation error for this specific field when user starts typing
    const fieldKey = field as 'bank_name' | 'beneficiary_name' | 'account_no' | 'sort_code' | 'iban_no' | 'bic' | 'amount';
    if (creditAccountErrors[index] && creditAccountErrors[index][fieldKey]) {
      setCreditAccountErrors(prev => {
        const updated = { ...prev };
        if (updated[index]) {
          const { [fieldKey]: _, ...rest } = updated[index];
          if (Object.keys(rest).length === 0) {
            delete updated[index];
          } else {
            updated[index] = rest;
          }
        }
        return updated;
      });
    }
    // Clear general error if all required fields are filled
    if (fieldErrors.credit_accounts) {
      const account = editForm.credit_accounts[index];
      const updatedAccount = { ...account, [field]: value };
      const requiredFields = ['bank_name', 'beneficiary_name', 'account_no', 'amount'];
      const allFilled = requiredFields.every(field => updatedAccount[field as keyof typeof updatedAccount]?.toString().trim());
      if (allFilled) {
        setFieldErrors(prev => ({ ...prev, credit_accounts: undefined }));
      }
    }
  };

  const handleSendForApproval = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!invoice) return;

        // Validate that sum of credit account amounts matches total amount
        if (editForm.credit_accounts && editForm.credit_accounts.length > 0) {
          const sumOfCreditAmounts = editForm.credit_accounts.reduce((sum, account) => {
            const amount = parseFloat(account.amount) || 0;
            return sum + amount;
          }, 0);
          
          const totalAmount = parseFloat(editForm.total_amount) || 0;
          
          // Allow small floating point differences (0.01)
          if (Math.abs(sumOfCreditAmounts - totalAmount) > 0.01) {
            setFieldErrors(prev => ({
              ...prev,
              total_amount: `Sum of Credit Account amounts (${sumOfCreditAmounts.toFixed(2)}) does not match Total Amount (${totalAmount.toFixed(2)})`
            }));
            errorModal(`Sum of Credit Account amounts (${sumOfCreditAmounts.toFixed(2)}) does not match Total Amount (${totalAmount.toFixed(2)})`);
            return;
          }
        }

    // Validate required fields first
    if (!validateFields()) {
      errorModal('Please fill in all required fields');
      return;
    }

    // Validate required approver fields for Send for Approval
    const approverIds = editForm.approvers.filter(id => Boolean(id));
    if (approverIds.length === 0) {
      setValidationMessages({ approvers: 'Select at least one approver' });
      errorModal('Please select at least one approver');
      return;
    }

    // Clear validation errors if all required fields are filled
    setValidationMessages({});

    setIsSendingForApproval(true);
    try {
      const formData = new FormData();
      formData.append('department', user?.department_id?.toString() || '');
      formData.append('vendor_id', editForm.vendor_id);
      formData.append('invoice_number', editForm.invoice_number);
      formData.append('invoice_date', editForm.invoice_date);
      formData.append('due_date', editForm.due_date);
      formData.append('total_amount', editForm.total_amount);
      formData.append('subtotal', editForm.subtotal || '');
      formData.append('tax_amount', editForm.tax_amount || '');
      formData.append('currency', editForm.currency || '');
      formData.append('description', editForm.description || '');
      formData.append('notes', editForm.notes || '');
      formData.append('po_number', editForm.po_number || '');
      
      // Append approvers as JSON array
      const approverIdsArray = approverIds.map(id => parseInt(id));
      formData.append('approvers', JSON.stringify(approverIdsArray));
      
      formData.append('payment_approver', editForm.payment_approver || '');
      formData.append('initiator_note', editForm.initiator_note || '');
      formData.append('payment_notes', editForm.payment_notes || '');
      if (invoice?.approval_type === 'Financial approval + Invoice Payment') {
        formData.append('reference_name', editForm.reference_name || '');
      }
      
      // Nastro account details (as JSON array)
      formData.append('nastro_accounts', JSON.stringify(editForm.nastro_accounts || []));
      
      // Credit account details (as JSON array)
      formData.append('credit_accounts', JSON.stringify(editForm.credit_accounts || []));

      // Add line items data
      formData.append('line_items', JSON.stringify(editableLineItems));

      if (editForm.file) {
        formData.append('invoice_file', editForm.file);
      }

      if (editForm.additional_attachments && editForm.additional_attachments.length > 0) {
        editForm.additional_attachments.forEach((file) => {
          formData.append('additional_attachments', file);
        });
      }

      const response = await vendorInvoicesAPI.update(invoice.id, formData);
      if (response.success) {
        toast.success('Invoice sent for approval successfully');
        navigate('/invoices');
        // Refresh the invoice data
        const updatedResponse = await vendorInvoicesAPI.getById(parseInt(id!));
        if (updatedResponse.success) {
          setInvoice(updatedResponse.data);
        }
      } else {
        errorModal(response.error || 'Error sending invoice for approval. Please try again.');
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 400) {
        errorModal(error.response?.data?.error || 'Error sending invoice for approval. Please try again.');
      } else {
        errorModal('Error sending invoice for approval. Please try again.');
      }
    } finally {
      setIsSendingForApproval(false);
    }
  };

  const handleApproverDecision = async (
    approverId: number,
    decision: 'approved' | 'rejected'
  ) => {
    if (!invoice) return;
    
    try {
      setIsSubmittingApproval(true);
      const notes = approverNotes[approverId] || '';

      // Call the approve API
      const response = await vendorInvoicesAPI.approve(
        invoice.id,
        decision,
        notes
      );
      
      if (response.success) {
        toast.success(`Invoice ${decision} successfully`);
        // Refresh invoice data
        const updatedResponse = await vendorInvoicesAPI.getById(parseInt(id!));
        if (updatedResponse.success) {
          setInvoice(updatedResponse.data);
          // Clear notes
          setApproverNotes({});
        }
        // Refresh audit trail
        const auditResponse = await vendorInvoicesAPI.getAuditTrail(parseInt(id!)).catch(() => ({ success: false, data: null }));
        if (auditResponse?.success) {
          setAuditTrail(auditResponse.data?.audit_trail || []);
        }
      } else {
        errorModal(response.error || `Failed to ${decision} invoice`);
      }
    } catch (error: any) {
      console.error('Approver decision failed:', error);
      errorModal(error?.response?.data?.error || `Failed to ${decision} invoice`);
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  // Line item management functions
  const addLineItem = () => {
    setEditableLineItems([...editableLineItems, {
      description: '',
      quantity: 1,
      unit_price: 0,
      amount: 0,
      isNew: true
    }]);
  };

  const updateLineItem = (index: number, field: string, value: string | number) => {
    const updatedItems = [...editableLineItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // Auto-calculate amount when quantity or unit_price changes
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = field === 'quantity' ? value as number : updatedItems[index].quantity;
      const unitPrice = field === 'unit_price' ? value as number : updatedItems[index].unit_price;
      updatedItems[index].amount = quantity * unitPrice;
    }

    setEditableLineItems(updatedItems);
  };

  // Calculate total amount: Amount (Excluding Vat/Tax) + Vat/Tax Amount = Total Amount
  const calculateTotalAmount = (subtotal: string, taxAmount: string): string => {
    const subtotalValue = parseFloat(subtotal) || 0;
    const taxValue = parseFloat(taxAmount) || 0;
    const total = subtotalValue + taxValue;
    return parseFloat(total.toFixed(2)).toString();
  };

  const removeLineItem = (index: number) => {
    const updatedItems = editableLineItems.filter((_, i) => i !== index);
    setEditableLineItems(updatedItems);
    // Don't recalculate subtotal or total_amount when deleting line items
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-primary-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-primary-100 text-primary-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const currentCurrency = editForm.currency || invoice?.currency || '';
  const currencySymbol = getCurrencySymbol(currentCurrency || 'GBP');
  const currencyFontClass = getCurrencyFontClass(currentCurrency || 'GBP');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error || 'Invoice not found'}</p>
          </div>
          <button
            onClick={() => navigate('/invoices')}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            Back to Invoices
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="w-full px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/invoices')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Invoices</span>
          </button>
          <div className="h-6 w-px bg-gray-300" />
          <h1 className="text-2xl font-bold text-gray-900">
            Invoice Details
          </h1>
        </div>
        <div className="flex items-center space-x-3">
          {/* {!isEditing && (
            <>
              <button
                onClick={handleEdit}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
              >
                <Edit className="h-4 w-4" />
                <span>Edit</span>
              </button>
            </>
          )} */}
        </div>
      </div>

      {/* Invoice Information */}
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Invoice Information</h2>
                <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invoice.status)}`}>
                  {getStatusIcon(invoice.status)}
                  <span className="capitalize">{invoice.status}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                  <div ref={departmentDropdownRef} className="relative">
                    <button
                      type="button"
                      disabled
                      onClick={() => {
                        setDepartmentDropdownOpen(!departmentDropdownOpen);
                      }}
                      className={`input-field w-full flex items-center justify-between text-left ${fieldErrors.department_id ? 'border-red-500' : ''}`}
                    >
                      <span className={editForm.department_id ? 'text-gray-900' : 'text-gray-500'}>
                        {user?.department_name || 'Select department'}
                        {/* {editForm.department_id
                          ? departments?.find(d => d.id.toString() === editForm.department_id)?.department || 'Select department'
                          : 'Select department'} */}
                      </span>
                      {/* <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${departmentDropdownOpen ? 'transform rotate-180' : ''}`} /> */}
                    </button>
                    {/* {departmentDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search departments..."
                              value={departmentSearchTerm}
                              onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {departments && departments.length > 0 ? (
                            departments
                              .filter(dept =>
                                dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                              )
                              .map((dept) => (
                                <button
                                  key={dept.id}
                                  type="button"
                                  className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                  onClick={() => {
                                    setEditForm({ ...editForm, department_id: dept.id.toString(), vendor_id: '' });
                                    setDepartmentDropdownOpen(false);
                                    setDepartmentSearchTerm('');
                                    setFieldErrors(prev => ({ ...prev, department_id: undefined }));
                                  }}
                                >
                                  {dept.department}
                                </button>
                              ))
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              No departments found
                            </div>
                          )}
                          {departments && departments.length > 0 && departments.filter(dept =>
                            dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                          ).length === 0 && (
                              <div className="px-4 py-2 text-sm text-gray-500 text-center">
                                No departments found
                              </div>
                            )}
                        </div>
                      </div>
                    )} */}
                  </div>
                  {fieldErrors.department_id && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.department_id}</p>
                  )}
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                  <div ref={vendorDropdownRef} className="relative">
                    <button
                      type="button"
                      disabled={!editForm.department_id || invoice.approval_type === 'Invoice Payment - Financial approval available'}
                      onClick={() => {
                        if (editForm.department_id) {
                          setVendorDropdownOpen(!vendorDropdownOpen);
                        }
                      }}
                      className={`input-field w-full flex items-center justify-between text-left ${!editForm.department_id ? 'opacity-50 cursor-not-allowed' : ''} ${fieldErrors.vendor_id ? 'border-red-500' : ''}`}
                    >
                      <span className={editForm.vendor_id ? 'text-gray-900' : 'text-gray-500'}>
                        {!editForm.department_id
                          ? 'Select department first'
                          : editForm.vendor_id
                            ? vendors?.find(v => v.id.toString() === editForm.vendor_id)?.name || 'Select vendor'
                            : 'Select vendor'}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${vendorDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    {vendorDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search vendors..."
                              value={vendorSearchTerm}
                              onChange={(e) => setVendorSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {vendors && vendors.length > 0 ? (
                            vendors
                              .filter(vendor => {
                                const vendorName = vendor?.name ?? '';
                                return vendorName
                                  .toString()
                                  .toLowerCase()
                                  .includes((vendorSearchTerm || '').toLowerCase());
                              })
                              .map((vendor) => (
                                <button
                                  key={vendor.id}
                                  type="button"
                                  className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                  onClick={() => {
                                    setEditForm({ ...editForm, vendor_id: vendor.id.toString() });
                                    setVendorDropdownOpen(false);
                                    setVendorSearchTerm('');
                                    setFieldErrors(prev => ({ ...prev, vendor_id: undefined }));
                                  }}
                                >
                                  {vendor.name}
                                </button>
                              ))
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              No vendors found
                            </div>
                          )}
                          {vendors && vendors.length > 0 && vendors.filter(vendor => {
                            const vendorName = vendor?.name ?? '';
                            return vendorName
                              .toString()
                              .toLowerCase()
                              .includes((vendorSearchTerm || '').toLowerCase());
                          }).length === 0 && (
                              <div className="px-4 py-2 text-sm text-gray-500 text-center">
                                No vendors found
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                  {fieldErrors.vendor_id && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.vendor_id}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                  <select
                    required
                    className={`input-field ${fieldErrors.currency ? 'border-red-500' : ''}`}
                    value={editForm.currency}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditForm((prev) => ({ ...prev, currency: value }));
                      setFieldErrors((prev) => ({ ...prev, currency: undefined }));
                      setInvoice((prev) => (prev ? { ...prev, currency: value } : prev));
                    }}
                    disabled={currencies.length === 0}
                  >
                    <option value="">
                      {currencies.length === 0 ? 'No currencies available' : 'Select currency'}
                    </option>
                    {currencies.map((currency) => (
                      <option key={currency.id} value={currency.name}>
                        {currency.name}
                        {currency.is_default ? ' (Default)' : ''}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.currency && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.currency}</p>
                  )}
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number *</label>
                  <input
                    type="text"
                    required
                    className={`input-field ${validationMessages.invoice_number || fieldErrors.invoice_number ? 'border-red-500' : ''}`}
                    value={editForm.invoice_number}
                    placeholder='Enter invoice number'
                    onChange={(e) => {
                      setEditForm({ ...editForm, invoice_number: e.target.value });
                      setFieldErrors(prev => ({ ...prev, invoice_number: undefined }));
                    }}
                  />
                  {fieldErrors.invoice_number && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.invoice_number}</p>
                  )}
                  {validationMessages.invoice_number && !fieldErrors.invoice_number && (
                    <p className="text-red-500 text-xs mt-1">{validationMessages.invoice_number}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date *</label>
                  <input
                    type="date"
                    required
                    className={`input-field ${fieldErrors.invoice_date ? 'border-red-500' : ''}`}
                    value={editForm.invoice_date}
                    placeholder='Enter invoice date'
                    onChange={(e) => {
                      setEditForm({ ...editForm, invoice_date: e.target.value });
                      setFieldErrors(prev => ({ ...prev, invoice_date: undefined }));
                    }}
                  />
                  {fieldErrors.invoice_date && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.invoice_date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                  <input
                    type="date"
                    required
                    className={`input-field ${fieldErrors.due_date ? 'border-red-500' : ''}`}
                    value={editForm.due_date}
                    min={editForm.invoice_date}
                    placeholder='Enter due date'
                    onChange={(e) => {
                      setEditForm({ ...editForm, due_date: e.target.value });
                      setFieldErrors(prev => ({ ...prev, due_date: undefined }));
                    }}
                  />
                  {fieldErrors.due_date && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.due_date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (Excluding Vat/Tax) *
                    <span className={`ml-1 text-xs text-gray-500 ${currencyFontClass}`}>({currencySymbol})</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    className={`input-field ${fieldErrors.subtotal ? 'border-red-500' : ''}`}
                    value={editForm.subtotal}
                    placeholder='Enter amount (Excluding Vat/Tax)'
                    onChange={(e) => {
                      const newSubtotal = e.target.value;
                      // Prevent negative numbers
                      if (newSubtotal !== '' && parseFloat(newSubtotal) < 0) {
                        return;
                      }
                      const newTotal = calculateTotalAmount(newSubtotal, editForm.tax_amount);
                      setEditForm({ 
                        ...editForm, 
                        subtotal: newSubtotal,
                        total_amount: newTotal
                      });
                      setFieldErrors(prev => ({ ...prev, subtotal: undefined }));
                    }}
                  />
                  {fieldErrors.subtotal && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.subtotal}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vat/Tax Amount *
                    <span className={`ml-1 text-xs text-gray-500 ${currencyFontClass}`}>({currencySymbol})</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    className={`input-field ${fieldErrors.tax_amount ? 'border-red-500' : ''}`}
                    value={editForm.tax_amount}
                    placeholder='Enter Vat/Tax amount'
                    onChange={(e) => {
                      const newTaxAmount = e.target.value;
                      // Prevent negative numbers
                      if (newTaxAmount !== '' && parseFloat(newTaxAmount) < 0) {
                        return;
                      }
                      const newTotal = calculateTotalAmount(editForm.subtotal, newTaxAmount);
                      setEditForm({ 
                        ...editForm, 
                        tax_amount: newTaxAmount,
                        total_amount: newTotal
                      });
                      setFieldErrors(prev => ({ ...prev, tax_amount: undefined }));
                    }}
                  />
                  {fieldErrors.tax_amount && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.tax_amount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Amount *
                    <span className={`ml-1 text-xs text-gray-500 ${currencyFontClass}`}>({currencySymbol})</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    className={`input-field bg-gray-50 cursor-not-allowed ${fieldErrors.total_amount ? 'border-red-500' : ''}`}
                    value={editForm.total_amount}
                    placeholder='Enter total amount'
                  />
                  {fieldErrors.total_amount && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.total_amount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PO Number (Optional)</label>
                  <input
                    type="text"
                    className="input-field"
                    value={editForm.po_number}
                    onChange={(e) => setEditForm({ ...editForm, po_number: e.target.value })}
                    placeholder='Enter PO number'
                  />
                </div>
              </div>

            </div>

            {/* Vendor Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-md font-semibold text-gray-900 mb-3">Vendor Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <UserIcon className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Vendor Name</p>
                    <p className="font-medium text-gray-900">{invoice?.vendor_name || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Vendor Email</p>
                    <p className="font-medium text-gray-900">{invoice?.vendor_email || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Vendor Phone</p>
                    <p className="font-medium text-gray-900">{invoice?.vendor_phone || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
          <div className="mb-6 mt-12">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Line Items</h3>
              <button
                type="button"
                onClick={addLineItem}
                className="flex items-center space-x-2 bg-primary-600 text-white px-3 py-1 rounded-md hover:bg-primary-700 transition-colors text-sm"
              >
                <Plus className="h-4 w-4" />
                <span>Add Item</span>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                {editableLineItems.length > 0 && (
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Description</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Qty</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Unit Price</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                )}
                <tbody className="bg-white divide-y divide-gray-200">
                  {
                    editableLineItems.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            className="input-field text-sm"
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            placeholder="Item description"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="input-field text-sm text-right"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="input-field text-sm text-right"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          />
                        </td>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                          <span className={currencyFontClass}>
                            {formatCurrency(item.amount, currentCurrency || 'GBP')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="text-red-600 hover:text-red-800"
                            title="Remove item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
          {/* { (
          <div className=" flex justify-end space-x-2">
            <button
              onClick={handleCancelEdit}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              disabled={isSaving || isSendingForApproval}
            >
              <span>Cancel</span>
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || isSendingForApproval}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Spinner />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save</span>
              )}
            </button>
          </div>
            )} */}
        </div>

        {/* Line Items */}


        {/* Additional Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Initiation</h3>

          {(
            <>
            <div className="space-y-6">
              {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    rows={3}
                    className="input-field"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  />
                </div> */}

              {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    className="input-field"
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </div> */}
                {/* Top Section - Full Width Fields */}
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Initiator Notes</label>
                    <textarea
                      rows={3}
                      className="input-field"
                      value={editForm.initiator_note}
                      onChange={(e) => setEditForm({ ...editForm, initiator_note: e.target.value })}
                      placeholder="Enter initiator notes..."
                    />
                  </div>

                  {/* Reference Name - Only show when approval type is "Financial approval + Invoice Payment" */}
                  {invoice?.approval_type === 'Financial approval + Invoice Payment' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reference Name *</label>
                      <input
                        type="text"
                        required
                        className={`input-field ${fieldErrors.reference_name ? 'border-red-500' : ''}`}
                        value={editForm.reference_name}
                        onChange={(e) => {
                          setEditForm({ ...editForm, reference_name: e.target.value });
                          setFieldErrors(prev => ({ ...prev, reference_name: undefined }));
                        }}
                        placeholder="Enter reference name..."
                      />
                      {fieldErrors.reference_name && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.reference_name}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Account Details Section - 2 Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Nastro Account Details Section */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <label className="block text-sm font-semibold text-gray-900 mb-3">Nostro Account Details *</label>
                    <button
                      type="button"
                      onClick={addNastroAccount}
                      className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-800 mb-3"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Account</span>
                    </button>
                    <div className="space-y-4">
                      {editForm.nastro_accounts.map((account, index) => (
                        <div key={index} className="space-y-3 relative bg-white p-4 rounded border border-gray-200">
                          {editForm.nastro_accounts.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeNastroAccount(index)}
                              className="absolute top-2 right-2 text-red-600 hover:text-red-800 p-1"
                              title="Remove account"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Account No *</label>
                            <input
                              type="text"
                              required
                              className={`input-field ${nastroAccountErrors[index]?.account_no ? 'border-red-500' : ''}`}
                              value={account.account_no}
                              onChange={(e) => updateNastroAccount(index, 'account_no', e.target.value)}
                              placeholder="Enter account number..."
                            />
                            {nastroAccountErrors[index]?.account_no && (
                              <p className="text-red-500 text-xs mt-1">{nastroAccountErrors[index].account_no}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Account Name *</label>
                            <input
                              type="text"
                              required
                              className={`input-field ${nastroAccountErrors[index]?.account_name ? 'border-red-500' : ''}`}
                              value={account.account_name}
                              onChange={(e) => updateNastroAccount(index, 'account_name', e.target.value)}
                              placeholder="Enter account name..."
                            />
                            {nastroAccountErrors[index]?.account_name && (
                              <p className="text-red-500 text-xs mt-1">{nastroAccountErrors[index].account_name}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Narration</label>
                            <textarea
                              rows={3}
                              className="input-field"
                              value={account.narration}
                              onChange={(e) => updateNastroAccount(index, 'narration', e.target.value)}
                              placeholder="Enter narration..."
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {fieldErrors.nastro_accounts && (
                      <p className="text-red-500 text-xs mt-2">{fieldErrors.nastro_accounts}</p>
                    )}
                  </div>

                  {/* Credit Account Details Section */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <label className="block text-sm font-semibold text-gray-900 mb-3">Credit Account Details *</label>
                    <button
                      type="button"
                      onClick={addCreditAccount}
                      className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-800 mb-3"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Account</span>
                    </button>
                    <div className="space-y-4">
                      {editForm.credit_accounts.map((account, index) => (
                        <div key={index} className="space-y-3 relative bg-white p-4 rounded border border-gray-200">
                          {editForm.credit_accounts.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeCreditAccount(index)}
                              className="absolute top-2 right-2 text-red-600 hover:text-red-800 p-1"
                              title="Remove account"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                            <input
                              type="text"
                              required
                              className={`input-field ${creditAccountErrors[index]?.bank_name ? 'border-red-500' : ''}`}
                              value={account.bank_name}
                              onChange={(e) => updateCreditAccount(index, 'bank_name', e.target.value)}
                              placeholder="Enter bank name (e.g., SEB)..."
                            />
                            {creditAccountErrors[index]?.bank_name && (
                              <p className="text-red-500 text-xs mt-1">{creditAccountErrors[index].bank_name}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Beneficiary Name *</label>
                            <input
                              type="text"
                              required
                              className={`input-field ${creditAccountErrors[index]?.beneficiary_name ? 'border-red-500' : ''}`}
                              value={account.beneficiary_name}
                              onChange={(e) => updateCreditAccount(index, 'beneficiary_name', e.target.value)}
                              placeholder="Enter beneficiary name..."
                            />
                            {creditAccountErrors[index]?.beneficiary_name && (
                              <p className="text-red-500 text-xs mt-1">{creditAccountErrors[index].beneficiary_name}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Account No *</label>
                            <input
                              type="text"
                              required
                              className={`input-field ${creditAccountErrors[index]?.account_no ? 'border-red-500' : ''}`}
                              value={account.account_no}
                              onChange={(e) => updateCreditAccount(index, 'account_no', e.target.value)}
                              placeholder="Enter account number..."
                            />
                            {creditAccountErrors[index]?.account_no && (
                              <p className="text-red-500 text-xs mt-1">{creditAccountErrors[index].account_no}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sort Code</label>
                            <input
                              type="text"
                              className={`input-field ${creditAccountErrors[index]?.sort_code ? 'border-red-500' : ''}`}
                              value={account.sort_code}
                              onChange={(e) => updateCreditAccount(index, 'sort_code', e.target.value)}
                              placeholder="Enter sort code..."
                            />
                            {creditAccountErrors[index]?.sort_code && (
                              <p className="text-red-500 text-xs mt-1">{creditAccountErrors[index].sort_code}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">IBAN No</label>
                            <input
                              type="text"
                              className={`input-field ${creditAccountErrors[index]?.iban_no ? 'border-red-500' : ''}`}
                              value={account.iban_no}
                              onChange={(e) => updateCreditAccount(index, 'iban_no', e.target.value)}
                              placeholder="Enter IBAN number..."
                            />
                            {creditAccountErrors[index]?.iban_no && (
                              <p className="text-red-500 text-xs mt-1">{creditAccountErrors[index].iban_no}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">BIC</label>
                            <input
                              type="text"
                              className={`input-field ${creditAccountErrors[index]?.bic ? 'border-red-500' : ''}`}
                              value={account.bic}
                              onChange={(e) => updateCreditAccount(index, 'bic', e.target.value)}
                              placeholder="Enter BIC..."
                            />
                            {creditAccountErrors[index]?.bic && (
                              <p className="text-red-500 text-xs mt-1">{creditAccountErrors[index].bic}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              required
                              className={`input-field ${creditAccountErrors[index]?.amount ? 'border-red-500' : ''}`}
                              value={account.amount}
                              onChange={(e) => {
                                const newAmount = e.target.value;
                                // Prevent negative numbers
                                if (newAmount !== '' && parseFloat(newAmount) < 0) {
                                  return;
                                }
                                updateCreditAccount(index, 'amount', newAmount);
                              }}
                              placeholder="Enter amount..."
                            />
                            {creditAccountErrors[index]?.amount && (
                              <p className="text-red-500 text-xs mt-1">{creditAccountErrors[index].amount}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Narration</label>
                            <textarea
                              rows={3}
                              className="input-field"
                              value={account.narration}
                              onChange={(e) => updateCreditAccount(index, 'narration', e.target.value)}
                              placeholder="Enter narration..."
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {fieldErrors.credit_accounts && (
                      <p className="text-red-500 text-xs mt-2">{fieldErrors.credit_accounts}</p>
                    )}
                  </div>
                </div>

                {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Processor Notes</label>
                    <textarea
                      rows={3}
                      className="input-field"
                      value={editForm.payment_notes}
                      onChange={(e) => setEditForm({ ...editForm, payment_notes: e.target.value })}
                      placeholder="Enter payment processor notes..."
                    />
                  </div> */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approvers *</label>
                <button
                  type="button"
                  onClick={handleAddApprover}
                  disabled={editForm.approvers.length >= 10}
                  className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Approver</span>
                </button>
                <div className="space-y-2">
                  {editForm.approvers.map((approverId, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div ref={(el) => (approverDropdownRefs.current[index] = el)} className="relative flex-1">
                        <button
                          type="button"
                          onClick={() => {
                            setApproverDropdownOpenIndex(approverDropdownOpenIndex === index ? null : index);
                            setVendorDropdownOpen(false);
                            setDepartmentDropdownOpen(false);
                          }}
                          className={`input-field w-full flex items-center justify-between text-left ${validationMessages.approvers ? 'border-red-500' : ''}`}
                        >
                          <span className={approverId ? 'text-gray-900' : 'text-gray-500'}>
                            {approverId
                              ? invoiceApprovers.find(a => a.id.toString() === approverId) 
                                ? `${invoiceApprovers.find(a => a.id.toString() === approverId)?.first_name} ${invoiceApprovers.find(a => a.id.toString() === approverId)?.last_name} (${invoiceApprovers.find(a => a.id.toString() === approverId)?.email})`
                                : 'Select approver'
                              : 'Select approver'}
                          </span>
                          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${approverDropdownOpenIndex === index ? 'transform rotate-180' : ''}`} />
                        </button>
                        {approverDropdownOpenIndex === index && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                            <div className="p-2 border-b border-gray-200">
                              <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                                <input
                                  type="text"
                                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                  placeholder="Search approvers..."
                                  value={approverSearchTerms[index] || ''}
                                  onChange={(e) => {
                                    const newTerms = [...approverSearchTerms];
                                    newTerms[index] = e.target.value;
                                    setApproverSearchTerms(newTerms);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              {invoiceApprovers.filter((user) => {
                                const needle = (approverSearchTerms[index] ?? '').toLowerCase();
                                const haystack = `${user.first_name} ${user.last_name} ${user.email ?? ''}`.toLowerCase();
                                return haystack.includes(needle);
                              }).map((user) => (
                                <button
                                  key={user.id}
                                  type="button"
                                  className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                  onClick={() => handleApproverSelect(index, user.id.toString())}
                                >
                                  {user.first_name} {user.last_name} ({user.email})
                                </button>
                              ))}
                              {invoiceApprovers.filter((user) => {
                                const needle = (approverSearchTerms[index] ?? '').toLowerCase();
                                const haystack = `${user.first_name} ${user.last_name} ${user.email ?? ''}`.toLowerCase();
                                return haystack.includes(needle);
                              }).length === 0 && (
                                <div className="px-4 py-2 text-sm text-gray-500 text-center">No approvers found</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {editForm.approvers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveApprover(index)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Remove approver"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {validationMessages.approvers && (
                  <p className="text-red-500 text-xs mt-1">{validationMessages.approvers}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Attachments</label>
                <input
                  ref={additionalAttachmentsInputRef}
                  type="file"
                  accept=".pdf"
                  multiple
                  className="input-field"
                  onChange={(e) => {
                    const newFiles = e.target.files ? Array.from(e.target.files) : [];
                    const oversizedFiles = newFiles.filter((file) => file.size > MAX_ATTACHMENT_SIZE_BYTES);
                    if (oversizedFiles.length > 0) {
                      const fileNames = oversizedFiles.map((file) => file.name).join(', ');
                      errorModal(
                        oversizedFiles.length === 1
                          ? `${fileNames} exceeds the 10MB limit. Please upload a file smaller than 10MB.`
                          : `${fileNames} exceed the 10MB limit. Please upload a file smaller than 10MB.`
                      );
                    }
                    const validNewFiles = newFiles.filter((file) => file.size <= MAX_ATTACHMENT_SIZE_BYTES);
                    const existingFiles = editForm.additional_attachments || [];
                    const combined = [...existingFiles, ...validNewFiles];
                    const deduped: File[] = [];
                    const seen = new Set<string>();
                    combined.forEach((f) => {
                      const key = `${f.name}-${f.size}-${(f as any).lastModified}`;
                      if (!seen.has(key)) {
                        seen.add(key);
                        deduped.push(f);
                      }
                    });
                    setEditForm({ ...editForm, additional_attachments: deduped });
                    if (additionalAttachmentsInputRef.current) {
                      additionalAttachmentsInputRef.current.value = '';
                    }
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">You can select multiple files. Maximum file size is 10MB.</p>

                {/* Show existing additional attachments */}
                {invoice.additional_attachments && invoice.additional_attachments.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current Attachments:</h4>
                    <div className="space-y-2">
                      {invoice.additional_attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                          <div className="flex items-center space-x-3">
                            <FileText className="h-5 w-5 text-primary-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{attachment.filename}</p>
                              <p className="text-xs text-gray-500">
                                {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : 'Unknown size'} • {attachment.type}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => window.open(attachment.url, '_blank')}
                              className="text-primary-600 hover:text-primary-800 p-1"
                              title="View file"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await vendorInvoicesAPI.deleteAdditionalAttachment(invoice.id, attachment.filepath);
                                  toast.success('Attachment deleted successfully');
                                  // Refresh invoice data
                                  const updatedResponse = await vendorInvoicesAPI.getById(parseInt(id!));
                                  if (updatedResponse.success) {
                                    setInvoice(updatedResponse.data);
                                  }
                                } catch (error) {
                                  console.error('Error deleting attachment:', error);
                                  errorModal('Failed to delete attachment');
                                }
                              }}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete file"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show newly uploaded files */}
                {editForm.additional_attachments && editForm.additional_attachments.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">New Files to Upload:</h4>
                    <div className="space-y-2">
                      {editForm.additional_attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-primary-50 rounded-lg border border-primary-200">
                          <div className="flex items-center space-x-3">
                            <FileText className="h-5 w-5 text-primary-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(1)} KB • {file.type}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedFiles = editForm.additional_attachments.filter((_, i) => i !== index);
                              setEditForm({ ...editForm, additional_attachments: updatedFiles });
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove file"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>


              {/* <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    onClick={handleCancelEdit}
                    className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                  >
                    <X className="h-4 w-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSubmitting}
                    className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    <span>{isSubmitting ? 'Saving...' : 'Save'}</span>
                  </button>
                </div> */}
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                  disabled={isSaving || isSendingForApproval}
                >
                  <span>Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={handleSendForApproval}
                  disabled={isSaving || isSendingForApproval}
                  className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSendingForApproval ? (
                    <>
                      <Spinner />
                      <span>Sending for Approval...</span>
                    </>
                  ) : (
                    <span>Send for Approval</span>
                  )}
                </button>
              </div>
            </div>
            </>
          )}
        </div>

        {/* Invoice Approval Flow - Always visible when approver_details exist */}
        {invoice?.approver_details && invoice.approver_details.length > 0 && (
          <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900">Invoice Approval Flow</h4>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {invoice.approver_details.map((approver: any, index: number) => {
                  const isCurrentUserApprover = String(user?.id || '') === String(approver.approver_id || '');
                  const canApprove = isCurrentUserApprover && 
                    approver.status !== 'approved' && 
                    approver.status !== 'rejected';
                  const isApproved = approver.status === 'approved';
                  const isRejected = approver.status === 'rejected';

                  return (
                    <div
                      key={approver.id || index}
                      className={`p-4 rounded-lg border transition-all duration-200 ${
                        isApproved
                          ? 'bg-green-50 border-green-200 hover:border-green-300'
                          : isRejected
                          ? 'bg-red-50 border-red-200 hover:border-red-300'
                          : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:border-orange-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider">
                            LEVEL {index + 1} APPROVER
                          </p>
                        </div>
                        <span
                          className={`text-xs font-semibold capitalize ${
                            isApproved
                              ? 'text-green-700'
                              : isRejected
                              ? 'text-red-700'
                              : 'text-gray-600'
                          }`}
                        >
                          {approver.status || 'Pending'}
                        </span>
                      </div>
                      <div
                        className={`bg-white rounded-lg p-3 border ${
                          isApproved
                            ? 'border-green-100'
                            : isRejected
                            ? 'border-red-100'
                            : 'border-orange-100'
                        }`}
                      >
                        <div className="flex items-center">
                          <div
                            className={`${
                              isApproved
                                ? 'bg-green-100'
                                : isRejected
                                ? 'bg-red-100'
                                : 'bg-gradient-to-br from-orange-100 to-orange-200'
                            } w-8 h-8 rounded-full flex items-center justify-center mr-3`}
                          >
                            <span
                              className={`text-xs font-semibold ${
                                isApproved
                                  ? 'text-green-700'
                                  : isRejected
                                  ? 'text-red-700'
                                  : 'text-orange-700'
                              }`}
                            >
                              {approver.first_name?.charAt(0) || ''}
                              {approver.last_name?.charAt(0) || ''}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {approver.first_name} {approver.last_name}
                            </p>
                            <p className="text-xs text-gray-600">{approver.email}</p>
                          </div>
                        </div>
                      </div>

                      {/* Note and action area */}
                      {canApprove ? (
                        <div className="mt-3 space-y-2">
                          <label className="block text-xs font-medium text-gray-700">Note</label>
                          <textarea
                            rows={3}
                            className="input-field w-full"
                            value={approverNotes[approver.approver_id] || ''}
                            onChange={(e) => {
                              setApproverNotes(prev => ({
                                ...prev,
                                [approver.approver_id]: e.target.value
                              }));
                            }}
                            placeholder="Enter your note..."
                          />
                          
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              disabled={isSubmittingApproval}
                              onClick={() => handleApproverDecision(approver.approver_id, 'rejected')}
                              className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              Reject
                            </button>
                            <button
                              type="button"
                              disabled={isSubmittingApproval}
                              onClick={() => handleApproverDecision(approver.approver_id, 'approved')}
                              className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              Approve
                            </button>
                          </div>
                        </div>
                      ) : approver.notes ? (
                        <div className="mt-3 p-3 bg-white border rounded">
                          <p className="text-xs font-semibold text-gray-700 mb-1">Note</p>
                          <p className="text-sm text-gray-800">{approver.notes}</p>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Audit Trail */}
      {auditTrail.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Audit Trail</h3>
          <div className="space-y-4">
            {auditTrail.map((entry, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <UserIcon className="h-4 w-4 text-primary-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">{entry.action}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(entry.created_at).toLocaleString()}
                    </p>
                  </div>
                  {entry.user && (
                    <p className="text-sm text-gray-600">By: {entry.user.name}</p>
                  )}
                  {entry.notes && (
                    <p className="text-sm text-gray-700 mt-1">{entry.notes}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetails;
